import React from 'react';
import { Icon, GDSIconSource } from '#components/icon/icon.tsx';
import { Spinner } from '#components/spinner/spinner.tsx';
import {
  mapResponsiveProp,
  useResponsiveProp,
} from '../../hooks/use-responsive-prop';
import {
  polymorphicForwardRef,
  type PolymorphicComponentPropWithRef,
  type PolymorphicRef,
  type GDSCustomizableComponent,
  type GDSTypographyIntrinsicEmphasis,
  type GDSResponsiveProp,
} from '../../types';
import { styled } from '../../theme';
import { useThemeStrings } from '../../hooks/use-theme';
import { GenesisCoreInspector } from '../../test-utils/genesis-core-inspector';

export type GDSTextActionIndicator =
  | 'disclosure'
  | 'newTabInternal'
  | 'externalLink';

export type GDSTextActionSize = 'medium' | 'large';

export type GDSTextActionProps<C extends React.ElementType> =
  PolymorphicComponentPropWithRef<
    C,
    {
      as?: 'a' | 'button' | React.ElementType;
      type?: 'button' | 'submit' | 'reset';
      children: React.ReactNode;
      size?: GDSResponsiveProp<GDSTextActionSize>;
      icon?: GDSIconSource;
      iconLabel?: string;
      destructive?: boolean;
      loading?: boolean;
      loadingText?: string;
      indicator?: GDSTextActionIndicator;
      indicatorLabel?: string;
      emphasis?: GDSTypographyIntrinsicEmphasis;
    }
  > &
    GDSCustomizableComponent;

export type GDSTextActionComponent = <C extends React.ElementType = 'a'>(
  props: GDSTextActionProps<C>,
) => React.ReactElement | null;

export const DISPLAY_NAME = 'TextAction';

const BaseTextAction = styled('a', {
  display: 'inline-flex',
  alignItems: 'center',
  textDecoration: 'none',
  position: 'relative',
  outline: 'none',
  background: 'none',
  border: 'none',
  borderRadius: '$small',
  cursor: 'pointer',
  padding: 0,
  '&:hover': {
    textDecoration: 'underline',
  },
  variants: {
    size: {
      medium: {
        typography: '$buttonTwo',
      },
      large: {
        typography: '$buttonOne',
      },
    },
    emphasis: {
      base: {
        color: '$interactiveActionPrimary',
      },
      subtle: {
        color: '$interactiveActionSubdued',
      },
    },
    destructive: {
      true: {
        color: '$onSurfaceTextCritical',
      },
    },
    loading: {
      true: {
        pointerEvents: 'none',
      },
    },
  },
});

const iconBySize = {
  medium: '20px',
  large: '24px',
};

const indicatorByString = {
  disclosure: 'tinyDisclosureIndicator',
  newTabInternal: 'tinyNewWindowInternal',
  externalLink: 'tinyExternalLink',
};

const getIconByIndicator = (indicator: GDSTextActionIndicator) =>
  indicatorByString[indicator];

export const TextAction: GDSTextActionComponent = polymorphicForwardRef(
  <C extends React.ElementType = 'a'>(
    {
      as: asElement,
      children,
      className,
      css,
      destructive,
      emphasis = 'base',
      icon,
      iconLabel,
      indicator,
      indicatorLabel,
      loading,
      loadingText,
      size = 'large',
      type,
      'data-testid': testId = `GDS-${DISPLAY_NAME}`,
      ...props
    }: GDSTextActionProps<C>,
    ref?: PolymorphicRef<C>,
  ) => {
    const iconSize = mapResponsiveProp<GDSTextActionSize>(size, iconBySize);

    const stringsMap = useThemeStrings();

    const labelByIndicator = {
      disclosure: stringsMap.disclosure,
      newTabInternal: stringsMap.newTabInternal,
      externalLink: stringsMap.externalLink,
    };

    const getLabelByIndicator = (indicatorIcon: GDSTextActionIndicator) =>
      labelByIndicator[indicatorIcon];

    if (emphasis === 'subtle' && destructive) {
      console.error(
        `Destructive is ${destructive} and emphasis is ${emphasis}. TextAction can only be either subtle or destructive.`,
      );
    }

    const defaultType = !type && asElement === 'button' ? 'button' : undefined;

    return (
      <GenesisCoreInspector displayName={DISPLAY_NAME}>
        <BaseTextAction
          as={asElement}
          className={['GDS-text-action', className].join(' ')}
          css={css}
          destructive={destructive}
          emphasis={emphasis}
          loading={loading}
          ref={ref}
          size={useResponsiveProp(size)}
          type={type || defaultType}
          data-testid={testId}
          {...props}
        >
          {loading && (
            <Spinner
              loading={loading}
              loadingText={loadingText}
              size={iconSize}
              css={{ marginInlineEnd: '$half', flexShrink: 0 }}
            />
          )}
          {icon && !loading && (
            <Icon
              icon={icon}
              size={iconSize}
              label={iconLabel}
              css={{ marginInlineEnd: '$half', flexShrink: 0 }}
            />
          )}
          {children}
          {indicator && (
            <Icon
              icon={getIconByIndicator(indicator)}
              size="$one"
              css={{ marginInlineStart: '$half', flexShrink: 0 }}
              label={indicatorLabel || getLabelByIndicator(indicator)}
            />
          )}
        </BaseTextAction>
      </GenesisCoreInspector>
    );
  },
);

// @ts-expect-error
TextAction.displayName = DISPLAY_NAME;
